import React, { forwardRef, useMemo } from 'react'
import { CaretDown } from '@phosphor-icons/react'
import { Icon, Popover, PopoverContent, PopoverTrigger } from '@shopflo/ui'
import { classNames } from 'src/lib/utils/helpers'

export type FilterChipVariant = 'accent' | 'default'
export type FilterChipSize = 'sm'

const chipBase =
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-md border whitespace-nowrap transition-colors duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-1'

const chipVariantClasses: Record<FilterChipVariant, string> = {
  accent:
    'border-dashed border-zinc-300 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 data-[active=true]:border-solid data-[active=true]:border-emerald-600 data-[active=true]:bg-emerald-500/10 data-[active=true]:text-emerald-700',
  default:
    'border-dashed border-zinc-300 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 data-[active=true]:border-solid data-[active=true]:border-zinc-400 data-[active=true]:bg-zinc-500/10 data-[active=true]:text-zinc-800'
}

const chipSizeClasses: Record<FilterChipSize, string> = {
  sm: 'h-[22px] px-1 text-text-xs-medium'
}

interface ChipVariantOptions {
  variant?: FilterChipVariant
  size?: FilterChipSize
  className?: string
}

export const chipVariants = ({ variant = 'accent', size = 'sm', className }: ChipVariantOptions = {}) =>
  classNames(chipBase, chipVariantClasses[variant], chipSizeClasses[size], className ?? '')

const countBadgeClasses = (active: boolean, variant: FilterChipVariant) =>
  classNames(
    'inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-sm px-1 text-xs leading-[18px]',
    active
      ? variant === 'accent'
        ? 'bg-emerald-500/15 text-emerald-700'
        : 'bg-zinc-200 text-zinc-700'
      : 'bg-zinc-100 text-zinc-500'
  )

export interface FilterChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: FilterChipVariant
  size?: FilterChipSize
  active?: boolean
  count?: number
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ variant = 'accent', size = 'sm', active = false, count, className, children, ...props }, ref) => (
    <button ref={ref} type='button' data-active={active} className={chipVariants({ variant, size, className })} {...props}>
      <span className='px-0.5'>{children}</span>
      {typeof count === 'number' && <span className={classNames('-ml-0.5', countBadgeClasses(active, variant))}>{count}</span>}
    </button>
  )
)
FilterChip.displayName = 'FilterChip'

export interface FilterChipOption {
  label: string
  value: string
  count?: number
}

export interface FilterChipsProps {
  tabs: FilterChipOption[]
  value: string
  onChange: (value: string) => void
  variant?: FilterChipVariant
  size?: FilterChipSize
  maxVisible?: number
  leading?: React.ReactNode
  className?: string
}

const FilterChips: React.FC<FilterChipsProps> = ({
  tabs,
  value,
  onChange,
  variant = 'accent',
  size = 'sm',
  maxVisible,
  leading,
  className
}) => {
  const { visibleTabs, overflowTabs } = useMemo(() => {
    if (!maxVisible || tabs.length <= maxVisible) {
      return { visibleTabs: tabs, overflowTabs: [] as FilterChipOption[] }
    }
    const visible = tabs.slice(0, maxVisible)
    const overflow = tabs.slice(maxVisible)
    // Keep the active chip visible even if it lives in the overflow bucket.
    const activeInOverflow = overflow.find(tab => tab.value === value)
    if (activeInOverflow) {
      const swapped = visible[visible.length - 1]
      return {
        visibleTabs: [...visible.slice(0, -1), activeInOverflow],
        overflowTabs: [swapped, ...overflow.filter(tab => tab.value !== value)]
      }
    }
    return { visibleTabs: visible, overflowTabs: overflow }
  }, [tabs, maxVisible, value])

  const overflowActive = overflowTabs.some(tab => tab.value === value)

  return (
    <div className={classNames('flex items-center gap-1', className ?? '')}>
      {leading && (
        <>
          {leading}
          <div className='h-4 w-px shrink-0 bg-zinc-200 mx-1' />
        </>
      )}
      {visibleTabs.map(tab => (
        <FilterChip
          key={tab.value}
          variant={variant}
          size={size}
          active={tab.value === value}
          count={tab.count}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </FilterChip>
      ))}
      {overflowTabs.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <FilterChip variant={variant} size={size} active={overflowActive}>
              {`+${overflowTabs.length} More`}
              <Icon IconComponent={CaretDown} size='xs' className='text-current' />
            </FilterChip>
          </PopoverTrigger>
          <PopoverContent align='start' className='w-48 p-1'>
            <div className='flex flex-col'>
              {overflowTabs.map(tab => {
                const isActive = tab.value === value
                return (
                  <button
                    key={tab.value}
                    type='button'
                    onClick={() => onChange(tab.value)}
                    className={classNames(
                      'flex items-center justify-between rounded-md px-2 py-1.5 text-text-xs-medium transition-colors',
                      isActive ? 'bg-zinc-100 text-zinc-800' : 'text-zinc-600 hover:bg-zinc-50'
                    )}
                  >
                    <span>{tab.label}</span>
                    {typeof tab.count === 'number' && (
                      <span className='text-text-xs-medium text-zinc-500'>{tab.count}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

export default FilterChips
